import { jwtDecode } from 'jwt-decode';
import React, { createContext, useContext, useState } from 'react';

interface UserContextState {
    user: Api.User | null;
    setUserInfo: (user: Api.User | null) => void;
    department: Api.Department | null;
    setDepartmentInfo: (department: Api.Department | null) => void;
    setToken: (jwtToken: string) => void;
    userToken: string | null;
    userRoles: string[];
}

export const UserContext = createContext<UserContextState>({
    user: null,
    setUserInfo: () => { },
    department: null,
    setDepartmentInfo: () => { },
    setToken: () => { },
    userToken: null,
    userRoles: [],
});


const PUBLIC_PATHS = ['login', 'register', 'examregistration', 'athleticsregistration', 'privacy', 'medlemskontingent'];

const computeRedirect = (): boolean => {
    if (typeof window === 'undefined') return false;
    const token = localStorage.getItem('myToken');
    if (token) return false;
    const onPublic = PUBLIC_PATHS.some(p => window.location.href.toLowerCase().indexOf(p) !== -1);
    if (onPublic) return false;
    // Kick off the navigation synchronously so nothing of the protected page paints.
    window.location.replace('/login');
    return true;
};

export const UserProvider: React.FC = ({ children }) => {
    // Decide synchronously, before any children render, whether we need to redirect.
    // If we do, render nothing — the browser is already navigating away.
    const [redirecting] = useState<boolean>(() => computeRedirect());

    // Hydrate from localStorage synchronously so the first paint already has user data.
    const initial = (() => {
        if (typeof window === 'undefined') {
            return { user: null as Api.User | null, token: null as string | null, roles: [] as string[], department: null as Api.Department | null };
        }
        const tokenRaw = localStorage.getItem('myToken');
        const userRaw = localStorage.getItem('myUser');
        const departmentRaw = localStorage.getItem('myDepartment');
        let roles: string[] = [];
        if (tokenRaw) {
            try {
                const decoded: any = jwtDecode(tokenRaw);
                const r = decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
                if (r) roles = typeof r === 'string' ? [r] : r;
            } catch { /* ignore malformed token */ }
        }
        return {
            user: userRaw ? (JSON.parse(userRaw) as Api.User) : null,
            token: tokenRaw,
            roles,
            department: departmentRaw ? (JSON.parse(departmentRaw) as Api.Department) : null,
        };
    })();

    const [user, setUser] = useState<Api.User | null>(initial.user);
    const [userToken, setUserToken] = useState<string | null>(initial.token);
    const [userRoles, setUserRoles] = useState<string[]>(initial.roles);
    const [department, setDepartment] = useState<Api.Department | null>(initial.department);

    const setToken = (jwtToken: string) => {
        if (jwtToken == null) {
            localStorage.removeItem('myToken');
            setUserToken(null);
            setUserRoles([]);
            return;
        }
        localStorage.setItem('myToken', jwtToken);
        setUserToken(jwtToken);
        try {
            const decoded: any = jwtDecode(jwtToken);
            const roles = decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
            if (roles) {
                const rolesArray = typeof roles === 'string' ? [roles] : roles;
                setUserRoles(rolesArray);
            } else {
                setUserRoles([]);
            }
        } catch {
            setUserRoles([]);
        }
    };

    const setDepartmentInfo = (department: Api.Department) => {
        if (department == null) localStorage.removeItem('myDepartment')
        else localStorage.setItem('myDepartment', JSON.stringify(department));
        setDepartment(department)
    };

    const setUserInfo = (user: Api.User) => {
        if (user == null) localStorage.removeItem('myUser')
        else localStorage.setItem('myUser', JSON.stringify(user));

        setUser(user)
    };

    const value = { department, setDepartmentInfo, setToken, user, setUserInfo, userToken, userRoles };

    if (redirecting) return null;

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => useContext(UserContext);
